import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "@tanstack/react-form";
import {
	createInvitationMutation,
	deleteInvitationMutation,
	getAllInvitationsOptions,
} from "@/lib/client/@tanstack/react-query.gen";
import type { InviteUserRequest, Invitation } from "@/lib/client/types.gen";
import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { UserPlus, Mail, Trash2, CheckCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
	DataTable,
	DataTableColumnHeader,
	DataTableToolbar,
	DataTableFilterFaceted,
} from "@/components/data-table";
import { normalizeString } from "@/lib/utils";

export const Route = createFileRoute("/admin/configuration/invitations")({
	component: InvitationsPage,
});

function InvitationsPage() {
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [invitationToDelete, setInvitationToDelete] =
		useState<Invitation | null>(null);

	const { data: invitations, refetch } = useQuery(getAllInvitationsOptions());

	const createMutation = useMutation({
		...createInvitationMutation(),
		onSuccess: () => {
			toast.success("L'invitation a été envoyée avec succès par email");
			refetch();
			form.reset();
		},
		onError: (error: any) => {
			toast.error(
				error.response?.data?.message || "Impossible de créer l'invitation",
			);
		},
	});

	const deleteMutation = useMutation({
		...deleteInvitationMutation(),
		onSuccess: () => {
			toast.success("L'invitation a été supprimée avec succès");
			refetch();
			setShowDeleteDialog(false);
			setInvitationToDelete(null);
		},
		onError: () => {
			toast.error("Impossible de supprimer l'invitation");
		},
	});

	const form = useForm({
		defaultValues: {
			email: "",
			firstName: "",
			lastName: "",
			role: "ADMIN" as const,
		},
		onSubmit: async ({ value }) => {
			const request: InviteUserRequest = {
				email: value.email,
				firstName: value.firstName,
				lastName: value.lastName,
				role: value.role,
			};
			createMutation.mutate({ body: request });
		},
	});

	const handleDeleteClick = (invitation: Invitation) => {
		setInvitationToDelete(invitation);
		setShowDeleteDialog(true);
	};

	const confirmDelete = () => {
		if (invitationToDelete?.id) {
			deleteMutation.mutate({
				path: {
					id: invitationToDelete.id,
				},
			});
		}
	};

	const columns = useMemo<ColumnDef<Invitation>[]>(
		() => [
			{
				accessorKey: "email",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Email" />
				),
				cell: ({ row }) => (
					<div className="flex items-center gap-2 font-medium">
						<Mail className="w-4 h-4 text-gray-400" />
						{row.original.email}
					</div>
				),
				meta: { label: "Email" },
			},
			{
				id: "name",
				accessorFn: (row) => `${row.lastName} ${row.firstName}`,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Nom" />
				),
				cell: ({ row }) => (
					<span>
						{row.original.firstName} {row.original.lastName}
					</span>
				),
				filterFn: (row, columnId, filterValue: string) => {
					const searchValue = normalizeString(filterValue);
					const fullName = normalizeString(
						`${row.original.firstName} ${row.original.lastName}`
					);
					const reverseName = normalizeString(
						`${row.original.lastName} ${row.original.firstName}`
					);
					const email = normalizeString(row.original.email || "");
					return (
						fullName.includes(searchValue) ||
						reverseName.includes(searchValue) ||
						email.includes(searchValue)
					);
				},
				sortDescFirst: false,
				meta: { label: "Nom" },
			},
			{
				accessorKey: "role",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Rôle" />
				),
				cell: ({ row }) => (
					<Badge variant="outline">{row.original.role}</Badge>
				),
				meta: { label: "Rôle" },
			},
			{
				accessorKey: "createdAt",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Date d'envoi" />
				),
				cell: ({ row }) =>
					row.original.createdAt
						? format(new Date(row.original.createdAt), "dd/MM/yyyy HH:mm", {
								locale: fr,
							})
						: null,
				meta: {
					label: "Date d'envoi",
					textValue: (row: Invitation) =>
						row.createdAt ? format(new Date(row.createdAt), "dd/MM/yyyy HH:mm", { locale: fr }) : "",
				},
			},
			{
				id: "status",
				accessorFn: (row) => (row.used ? "true" : "false"),
				filterFn: (row, id, value: string[]) =>
					value.includes(String(row.getValue(id))),
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Statut" />
				),
				cell: ({ row }) => {
					if (row.original.used) {
						return (
							<Badge className="bg-green-100 text-green-800">
								<CheckCircle className="w-3 h-3 mr-1" />
								Utilisée
							</Badge>
						);
					}
					return (
						<Badge variant="secondary">
							<Clock className="w-3 h-3 mr-1" />
							En attente
						</Badge>
					);
				},
				meta: {
					label: "Statut",
					textValue: (row: Invitation) => row.used ? "Utilisée" : "En attente",
				},
			},
			{
				id: "actions",
				enableSorting: false,
				enableHiding: false,
				header: () => <span>Actions</span>,
				cell: ({ row }) => {
					if (row.original.used) return null;
					return (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => handleDeleteClick(row.original)}
						>
							<Trash2 className="w-4 h-4" />
						</Button>
					);
				},
				meta: { label: "Actions" },
			},
		],
		[],
	);

	const statusFilterOptions = [
		{ label: "Utilisée", value: "true", icon: CheckCircle },
		{ label: "En attente", value: "false", icon: Clock },
	];

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-2xl font-bold">Invitations</h1>
				<p className="text-gray-600 mt-2">
					Inviter de nouveaux administrateurs à rejoindre Revacours
				</p>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Nouvelle invitation</CardTitle>
					<CardDescription>
						Envoyer une invitation par email pour créer un compte administrateur
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
						className="space-y-4"
					>
						<div className="grid grid-cols-2 gap-4">
							<form.Field
								name="firstName"
								children={(field) => (
									<div>
										<Label htmlFor="firstName">Prénom</Label>
										<Input
											id="firstName"
											type="text"
											value={field.state.value}
											onChange={(e) => field.handleChange(e.target.value)}
											required
										/>
									</div>
								)}
							/>

							<form.Field
								name="lastName"
								children={(field) => (
									<div>
										<Label htmlFor="lastName">Nom</Label>
										<Input
											id="lastName"
											type="text"
											value={field.state.value}
											onChange={(e) => field.handleChange(e.target.value)}
											required
										/>
									</div>
								)}
							/>
						</div>

						<form.Field
							name="email"
							children={(field) => (
								<div>
									<Label htmlFor="email">Email</Label>
									<Input
										id="email"
										type="email"
										value={field.state.value}
										onChange={(e) => field.handleChange(e.target.value)}
										required
									/>
								</div>
							)}
						/>

						<form.Field
							name="role"
							children={(field) => (
								<div>
									<Label htmlFor="role">Rôle</Label>
									<Select
										value={field.state.value}
										onValueChange={(value) =>
											field.handleChange(value as "ADMIN")
										}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="ADMIN">Administrateur</SelectItem>
										</SelectContent>
									</Select>
								</div>
							)}
						/>

						<Button
							type="submit"
							className="w-full"
							disabled={createMutation.isPending}
						>
							{createMutation.isPending ? (
								<>Envoi en cours...</>
							) : (
								<>
									<UserPlus className="w-4 h-4 mr-2" />
									Envoyer l'invitation
								</>
							)}
						</Button>
					</form>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Invitations envoyées</CardTitle>
					<CardDescription>
						Liste des invitations envoyées et leur statut
					</CardDescription>
				</CardHeader>
				<CardContent>
					<DataTable
						columns={columns}
						data={invitations || []}
						emptyMessage="Aucune invitation envoyée"
						toolbar={(table) => (
							<DataTableToolbar table={table} showExportExcel exportFilename="invitations">
								{table.getColumn("status") && (
									<DataTableFilterFaceted
										column={table.getColumn("status")!}
										title="Statut"
										options={statusFilterOptions}
									/>
								)}
							</DataTableToolbar>
						)}
					/>
				</CardContent>
			</Card>

			<AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
						<AlertDialogDescription>
							Êtes-vous sûr de vouloir supprimer cette invitation ?
							L'utilisateur ne pourra plus utiliser le lien pour créer son
							compte.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => setInvitationToDelete(null)}>
							Annuler
						</AlertDialogCancel>
						<AlertDialogAction onClick={confirmDelete}>
							Supprimer
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
